import React from 'react';
import { motion } from 'framer-motion';
import { Flag, Settings, Tv, FlaskConical, Rocket } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { DEMO_URL } from '../constants';

const dayIcons = [Flag, Settings, Tv, FlaskConical, Rocket];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut', delay },
});

export default function Implementation({ onDemoClick }) {
  const { t } = useLanguage();
  const impl = t.implementation;

  return (
    <section className="py-14 md:py-20 bg-[#1F2833]" data-testid="implementation-section">
      <div className="section-container">
        <motion.div className="text-center mb-14" {...fadeUp()}>
          <div className="divider-gold mx-auto" />
          <h2 className="heading-lg mb-4">{impl.title}</h2>
          <p className="text-[#8A9099] text-base max-w-xl mx-auto">{impl.body}</p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Horizontal line (desktop) */}
          <div className="hidden lg:block absolute top-10 left-[5%] right-[5%] h-0.5 bg-gradient-to-r from-transparent via-[#0F6B49]/40 to-transparent" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {impl.days.map((d, i) => {
              const Icon = dayIcons[i];
              return (
                <motion.div key={i} className="relative flex flex-col items-center text-center"
                  {...fadeUp(0.1 + i * 0.1)} data-testid={`impl-day-${i}`}>
                  <div className="relative z-10 w-20 h-20 rounded-full flex flex-col items-center justify-center mb-5
                    bg-gradient-to-br from-[#0F6B49]/30 to-[#1F2833] border-2 border-[#0F6B49]/60
                    shadow-[0_0_20px_rgba(15,107,73,0.2)] hover:border-[#D5A63B]/60 transition-all duration-300">
                    <span className="text-[#D5A63B] text-[8px] font-black font-exo uppercase">{d.day}</span>
                    <Icon size={18} className="text-[#0F6B49] mt-0.5" />
                  </div>
                  <h3 className="font-exo font-bold italic text-[#D5A63B] text-base mb-2">{d.label}</h3>
                  <p className="text-[#8A9099] text-sm leading-relaxed">{d.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>

        <motion.div className="text-center mt-14" {...fadeUp(0.7)}>
          <a href={DEMO_URL} target="_blank" rel="noopener noreferrer"
            className="btn-primary text-base px-8 py-3.5 no-underline" data-testid="impl-cta-btn">
            {impl.cta}
          </a>
        </motion.div>
      </div>
    </section>
  );
}
